import { useMemo } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Alert,
} from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { EmptyState } from '@/components/EmptyState';
import { getSubjectAllocationDirect, type SubjectAllocationData } from '@timetable/api';

interface SubjectAllocationReportProps {
  academicYear: string | null;
  versionValidFrom: string | null;
}

const COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8',
  '#82CA9D', '#FFC658', '#FF6B9D', '#C084FC', '#34D399',
];

export function SubjectAllocationReport({ academicYear, versionValidFrom }: SubjectAllocationReportProps) {
  const { data: allocationData, isLoading, error } = useQuery({
    queryKey: ['subject-allocation-report', academicYear, versionValidFrom],
    queryFn: () => getSubjectAllocationDirect(
      academicYear ?? undefined,
      versionValidFrom ?? undefined
    ),
    enabled: !!academicYear && !!versionValidFrom,
  });

  const { chartData, totalPeriods } = useMemo(() => {
    if (!allocationData || allocationData.length === 0) {
      return { chartData: [], totalPeriods: 0 };
    }

    const total = allocationData.reduce((sum, subject) => sum + subject.total_periods, 0);

    const chartData = allocationData.map(subject => ({
      name: subject.subject_name,
      value: subject.total_periods,
      percentage: ((subject.total_periods / total) * 100).toFixed(1),
    }));

    return { chartData, totalPeriods: total };
  }, [allocationData]);

  const renderCustomLabel = ({ name, percentage }: { name: string; percentage: string }) => {
    return `${name}: ${percentage}%`;
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load subject allocation report. Please try again.
      </Alert>
    );
  }

  if (!allocationData || allocationData.length === 0) {
    return (
      <EmptyState
        title="No Data Available"
        description="No subject allocation data found for the selected period."
      />
    );
  }

  return (
    <Box>
      {/* Summary */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Allocation Summary
        </Typography>
        <Box sx={{ display: 'flex', gap: 4, mt: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Total Periods
            </Typography>
            <Typography variant="h4">{totalPeriods}</Typography>
            <Typography variant="caption" color="text.secondary">
              across all subjects
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Total Subjects
            </Typography>
            <Typography variant="h4">{allocationData.length}</Typography>
            <Typography variant="caption" color="text.secondary">
              with allocations
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Pie Chart Visualization */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Subject Distribution
        </Typography>
        <ResponsiveContainer width="100%" height={400}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomLabel}
              outerRadius={120}
              fill="#8884d8"
              dataKey="value"
            >
              {chartData.map((_entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(value: number) => `${value} periods`} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </Paper>

      {/* Detailed Table */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Subject Allocation Details
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Subject Code</TableCell>
                <TableCell>Subject Name</TableCell>
                <TableCell align="right">Total Periods</TableCell>
                <TableCell align="right">Percentage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allocationData.map((subject) => (
                <TableRow key={subject.subject_id}>
                  <TableCell>{subject.subject_code}</TableCell>
                  <TableCell>{subject.subject_name}</TableCell>
                  <TableCell align="right">{subject.total_periods}</TableCell>
                  <TableCell align="right">{subject.percentage.toFixed(1)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
